'use client';

import { useState, useEffect, useMemo, Suspense } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from '@carbon/icons-react';
import ChampionBanner from '@/components/BannerSection/ChampionBanner';
import ChampCarousel from '@/components/Carousel/ChampCarousel';
import ChampionFilters from '@/components/ChampionFilters/ChampionFilters';
import ChampionCard from '@/components/Cards/ChampionCard/ChampionCard';
import BottomBanner from '@/components/BannerSection/BottomBanner/BottomBanner';
import AnalyticsTracker from '@/analytics/AnalyticsTracker';
import ContentEngagementClient from '@/analytics/ContentEngagementClient';
import { cleanMetadata } from '@/utils/cleanMetadata';
import { thisHostAndBasePath } from '@/../config';
import { getAllChampions } from '@/apis/ChampionsAPIs';
import { CHAMPION_LOCATIONS, CHAMPION_SKILLS } from '@/app/(pages)/champion/champion-filters';
import type { Champion } from '@/app/types/profile';
import Loader from '@/components/Loader';

import './styles.scss';

const testimonials = [
  {
    id: '1',
    quote:
      'By contributing to the community, I sharpen my own skills, expand my network, and stay at the forefront of innovation.',
    name: 'Rikke Jacobsen',
    title: 'CEO',
    company: 'CogniTech A/S',
    image: 'https://dw1.s81c.com/developer-static-pages/open/en/home/images/rikke-champ.png',
  },
  {
    id: '2',
    quote:
      'I actively engage in technical discussions, aiming to empower the business automation community with innovative insights that drive transformation.',
    name: 'Atanu Roy',
    title: 'Lead Engineer',
    company: 'London Stock Exchange Group',
    image: 'https://dw1.s81c.com/developer-static-pages/open/en/home/images/Group-b-1.png',
  },
  {
    id: '3',
    quote: 'I just love helping others and sharing knowledge. Hidden knowledge helps nobody!',
    name: 'Roy Boxwell',
    title: 'Senior Software Architect',
    company: 'Software Engineering Gmbh',
    image: 'https://dw1.s81c.com/developer-static-pages/open/en/home/images/Group-b-2.png',
  },
];

const ITEMS_PER_PAGE = 9;

export default function MeetChampionsPage() {
  // Full dataset fetched once
  const [allChampions, setAllChampions] = useState<Champion[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  const pageMetaData = useMemo(() => cleanMetadata({
    slug: 'champion/meet-the-champions',
    publish_date: null,
    date_modified: null,
    url: `${thisHostAndBasePath}/community/ibm-champions/`,
    title: 'Meet the IBM Champions',
    favourite: '',
    like: '',
    follow: '',
    status: '',
    primary_category: '',
    tags: '',
    strategic_content_area: '',
    authors: '',
    authors_email: '',
    editors: '',
    editors_email: '',
    record_type: 'static-page',
    secondary_tag: '',
  }), []);

  // Fetch all champions once on mount
  useEffect(() => {
    getAllChampions().then(data => {
      setAllChampions(data ?? []);
      setLoading(false);
    });
  }, []);

  // Derive unique country list for the filter
  const availableCountries = useMemo(() => {
    const countries = new Set<string>();
    if (allChampions.length > 0) {
      allChampions.forEach(c => {
        if (c.country?.trim()) {
          countries.add(c.country.trim());
        }
      });
    } else {
      CHAMPION_LOCATIONS.forEach(loc => {
        const parts = loc.split(',');
        const country = parts[parts.length - 1]?.trim();
        if (country) countries.add(country);
      });
    }
    return Array.from(countries).sort((a, b) => a.localeCompare(b));
  }, [allChampions]);

  // Client-side filtering — runs instantly on every keystroke, no API call
  const filteredChampions = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const selectedLocationsLower = selectedLocations.map(l => l.toLowerCase().trim());
    return allChampions.filter(champion => {
      const fullName = `${champion.first_name ?? ''} ${champion.last_name ?? ''}`.toLowerCase();
      const country = (champion.country ?? '').toLowerCase().trim();

      const matchesSearch = term
        ? fullName.includes(term) ||
          (champion.role ?? '').toLowerCase().includes(term) ||
          (champion.city ?? '').toLowerCase().includes(term) ||
          (champion.country ?? '').toLowerCase().includes(term) ||
          (champion.skills ?? []).some(s => s.toLowerCase().includes(term))
        : true;

      const matchesLocation =
        selectedLocations.length === 0 || selectedLocationsLower.includes(country);

      const matchesSkills =
        selectedSkills.length === 0 ||
        selectedSkills.some(skill =>
          (champion.skills ?? []).some(s => s.toLowerCase() === skill.toLowerCase()),
        );

      return matchesSearch && matchesLocation && matchesSkills;
    });
  }, [allChampions, searchTerm, selectedLocations, selectedSkills]);

  // Pagination derived from filtered results
  const totalPages = Math.max(1, Math.ceil(filteredChampions.length / ITEMS_PER_PAGE));
  const paginatedChampions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredChampions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredChampions, currentPage]);

  // Handlers
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleLocationToggle = (location: string) => {
    setSelectedLocations(prev =>
      prev.includes(location) ? prev.filter(l => l !== location) : [...prev, location],
    );
    setCurrentPage(1);
  };

  const handleSkillToggle = (skill: string) => {
    setSelectedSkills(prev =>
      prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill],
    );
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedLocations([]);
    setSelectedSkills([]);
    setCurrentPage(1);
  };

  return (
    <div className="meet-the-champions-page">
      <Suspense fallback={null}>
        <AnalyticsTracker />
      </Suspense>
      <ContentEngagementClient metaData={pageMetaData} />

      <ChampionBanner
        title="IBM Champions are the ultimate advocates for IBM technologies"
        subtitle="Explore the advocates shaping IBM technologies and the path to join them"
        imageUrl="https://dw1.s81c.com/developer-static-pages/open/en/home/images/meet-the-champions.png"
        imageAlt="Meet the Champions"
        showButton={true}
        btnTxt="Start Your Journey"
        href="https://ibm.biz/mainpagebluebutton"
      />
      <br />
      <div className="meet-the-champions">
        <ChampCarousel testimonials={testimonials} autoPlaySpeed={5000} />
        <br />
        <section>
          <div className="champions-two-column">
            <div className="champ-h1">
              <h1>Program overview</h1>
            </div>
            <div>
              <p>
                Contributions from each Champion and advocate create an exponential impact on our
                product development, community engagement, and global reach.
              </p>

              <p className="intro-btn">
                <a
                  href="/community/champions-program/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Explore full IBM Champions program details (opens in new tab)"
                  className="intro-btn__link"
                >
                  Explore full program details <ArrowRight aria-hidden="true" />
                </a>
              </p>
            </div>
          </div>
        </section>

        <br />
        <hr />
        <br />

        {/* <section className="meet-the-champions__section">
          <div className="meet-the-champions__container">
            <aside className="meet-the-champions__filters">
              <ChampionFilters
                locations={availableCountries}
                skills={CHAMPION_SKILLS}
                searchTerm={searchTerm}
                selectedLocations={selectedLocations}
                selectedSkills={selectedSkills}
                championCount={filteredChampions.length}
                onSearchChange={handleSearchChange}
                onLocationToggle={handleLocationToggle}
                onSkillToggle={handleSkillToggle}
                onClearFilters={handleClearFilters}
              />
            </aside>

            <main className="meet-the-champions__grid-container">
              <div
                className={`champions-grid-wrapper${loading ? ' champions-grid-wrapper--loading' : ''}`}
              >
                {loading && (
                  <div className="champions-grid-overlay">
                    <Loader />
                  </div>
                )}
                {!loading && filteredChampions.length === 0 ? (
                  <div className="champions-empty">No champions found matching your filters.</div>
                ) : (
                  <div className="meet-the-champions__grid">
                    {paginatedChampions.map(champion => (
                      <ChampionCard key={champion.uuid} {...champion} />
                    ))}
                  </div>
                )}
              </div>

              {!loading && totalPages > 1 && (
                <div className="meet-the-champions__pagination">
                  <button
                    className="pagination-btn pagination-btn--prev"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft />
                  </button>

                  <span className="pagination-info">
                    {currentPage}/{totalPages}
                  </span>

                  <button
                    className="pagination-btn pagination-btn--next"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight />
                  </button>
                </div>
              )}
              <br />
            </main>
          </div>
        </section>

        <br />
        <hr /> */}
        <br />
        
        <section>
          <div className="champions-two-column">
            <div className="champ-h1">
              <h1>Community at the core</h1>
            </div>
            <div>
              <p>
                One of the best places to get to know Champions and advocates is through the IBM
                Communities. You will find blogs, discussion threads, and upcoming events.
              </p>
              <p className="intro-btn">
                <a
                  href="https://community.ibm.com/community/user/champions?CommunityKey=50275c5e-5c6e-4dc5-ae97-805203da2163"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get started at the IBM Champions Group "
                  className="intro-btn__link"
                >
                  Get started at the IBM Champions Group <ArrowRight aria-hidden="true" />
                </a>
              </p>
            </div>
          </div>
        </section>
        <br />
        <br />
      </div>
      <BottomBanner
        title="Recognized advocacy leadership starts here"
        description="Meet the Champions and advocates in the community"
        subHeader="Join a global network of technical experts who amplify impact, share knowledge, and shape the future of IBM technologies."
        buttonText="Join the community"
        buttonLink="https://community.ibm.com/community/user/champions?CommunityKey=50275c5e-5c6e-4dc5-ae97-805203da2163"
        backgroundColor="#f4f4f4"
      />
    </div>
  );
}
